/**
 * Mobile quick add.
 *
 * A single bottom drawer, shared by every product card, filled from the size
 * payload the card already carries in the DOM: the one on the selected color
 * swatch, or the one on the "+" button as a fallback (see pcard-size-data.liquid).
 * Reading the payload at open time is what covers the requirement of showing the
 * sizes of the color being looked at, without a round trip.
 */

/**
 * Adds a variant to the cart and lets the theme's cart drawer take over the
 * screen: CartDrawer listens to cart:refresh, re-fetches its section and opens
 * itself on detail.open.
 * @param {Number} variantId
 * @returns {Promise<String>} the error message, or '' when the item was added.
 */
async function quickAddToCart(variantId) {
  try {
    const config = FoxTheme.utils.fetchConfig('javascript');
    config.body = JSON.stringify({ id: variantId, quantity: 1 });

    const response = await fetch(FoxTheme.routes.cart_add_url, config);
    const parsedState = await response.json();

    if (!response.ok || parsedState.status) {
      return parsedState.description || parsedState.message || FoxTheme.cartStrings.error;
    }

    document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

    if (FoxTheme.settings.cartType === 'page') {
      window.location.href = FoxTheme.routes.cart_url;
      return '';
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));

    return '';
  } catch (error) {
    console.error('Quick add:', error);
    return FoxTheme.cartStrings.error;
  }
}

class QuickAddDrawer extends DrawerComponent {
  constructor() {
    super();

    this.selectors = {
      title: '[data-quick-add-title]',
      price: '[data-quick-add-price]',
      sizes: '[data-quick-add-sizes]',
      error: '[data-quick-add-error]',
      sizeTemplate: '[data-quick-add-size-template]',
      size: '[data-quick-add-size]',
      sizeLabel: '.quick-add-drawer__size-label',
      spinner: '.loading__spinner',
      cardTitle: '.product-card__title .reversed-link__text',
      cardPrice: '.product-card__price .f-price',
      selectedSwatch: '.swatch-item[aria-selected="true"]',
    };

    this.isAdding = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.onSizeClick.bind(this), {
      signal: this.abortController.signal,
    });
  }

  /**
   * Copies title, price and sizes from the card to the drawer.
   * @param {HTMLElement} button the "+" that was tapped
   * @returns {Boolean} false when the card has no sizes to show, so the caller
   * can fall back to the quick add action of the card itself.
   */
  populate(button) {
    const card = button.closest('.product-card');
    if (!card) return false;

    const sizes = this.readSizes(button, card);
    if (!sizes.length) return false;

    this.renderSizes(sizes);
    this.setError('');

    const title = card.querySelector(this.selectors.cardTitle);
    this.querySelector(this.selectors.title).textContent = title ? title.textContent.trim() : '';

    const price = card.querySelector(this.selectors.cardPrice);
    this.querySelector(this.selectors.price).innerHTML = price ? price.outerHTML : '';

    return true;
  }

  /**
   * The selected swatch wins: ColorSwatch.select() moves the aria-selected when
   * a color is tapped, so its payload always belongs to the color on screen. The
   * one on the button covers cards without swatches and the ones that trim
   * colors with pcard_maximum_swatches_to_show.
   * @returns {Array} [id, size, available] tuples
   */
  readSizes(button, card) {
    const swatch = card.querySelector(this.selectors.selectedSwatch);
    const payload = (swatch && swatch.dataset.quickAddSizes) || button.dataset.quickAddSizes;
    if (!payload) return [];

    try {
      return JSON.parse(payload);
    } catch (error) {
      console.error('Quick add: invalid size payload', error);
      return [];
    }
  }

  renderSizes(sizes) {
    const template = this.querySelector(this.selectors.sizeTemplate);
    const fragment = document.createDocumentFragment();

    sizes.forEach(([id, label, available]) => {
      const chip = template.content.firstElementChild.cloneNode(true);

      chip.querySelector(this.selectors.sizeLabel).textContent = label;
      chip.dataset.variantId = id;

      if (!available) {
        chip.classList.add('quick-add-drawer__size--unavailable');
        chip.disabled = true;
      }

      fragment.appendChild(chip);
    });

    this.querySelector(this.selectors.sizes).replaceChildren(fragment);
  }

  onSizeClick(event) {
    const chip = event.target.closest(this.selectors.size);
    if (!chip || chip.disabled || this.isAdding) return;

    this.addSize(chip);
  }

  async addSize(chip) {
    this.isAdding = true;
    this.setLoading(chip, true);
    this.setError('');

    const errorMessage = await quickAddToCart(Number(chip.dataset.variantId));

    this.isAdding = false;
    this.setLoading(chip, false);

    if (errorMessage) {
      this.setError(errorMessage);
      return;
    }

    // The cart drawer opens once it finishes fetching its section; closing in
    // parallel keeps the two transitions from stacking on top of each other.
    this.hide();
  }

  setLoading(chip, isLoading) {
    chip.classList.toggle('quick-add-drawer__size--loading', isLoading);

    const spinner = chip.querySelector(this.selectors.spinner);
    if (spinner) spinner.classList.toggle('hidden', !isLoading);

    this.querySelectorAll(this.selectors.size).forEach((size) => {
      size.toggleAttribute('aria-disabled', isLoading);
    });
  }

  setError(message) {
    const element = this.querySelector(this.selectors.error);
    element.textContent = message;
    element.hidden = !message;
  }
}
customElements.define('quick-add-drawer', QuickAddDrawer);

/**
 * Cards with no sizes to show — a single variant, quantity rules, or a product
 * whose only option is the color — delegate to the quick add action the card
 * already renders: the add to cart form, the "choose options" popup or the link
 * to the PDP, depending on settings.pcard_choose_options_actions.
 */
function quickAddFallback(button) {
  const card = button.closest('.product-card');
  const action = card && card.querySelector('.product-card__main-actions [type="submit"], .product-card__atc');

  if (action) {
    action.click();
    return;
  }

  const url = button.getAttribute('data-quick-add-url');
  if (url) window.location.href = url;
}

// Delegated on document: facets and AJAX pagination re-render the grid, and
// ModalComponent binds its [aria-controls] only once.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-quick-add-mobile]');
  if (!button) return;

  event.preventDefault();

  const drawer = document.getElementById('QuickAddDrawer');

  if (drawer && typeof drawer.populate === 'function' && drawer.populate(button)) {
    drawer.show(button);
    return;
  }

  quickAddFallback(button);
});
